import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import NpdQuoteSheet from "@/components/NpdQuoteSheet";
import ProjectSourcingRollup from "@/components/ProjectSourcingRollup";
import { computeNpd } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";

export default function ProjectPreview() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, c, s, u] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get("/customers"),
          api.get("/settings/public"),
          api.get("/units"),
        ]);
        setProject(p.data);
        setCustomers(c.data || []);
        setSettings(s.data || {});
        setUnits(u.data || []);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Failed to load project";
        setLoadError(msg);
        toast.error(msg);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (loadError) return (
    <div className="p-10 text-center">
      <div className="text-red-600 font-medium mb-2">Could not load preview</div>
      <div className="text-sm text-slate-500">{loadError}</div>
      <Link to={`/projects/${id}`} className="inline-block mt-4 text-sm text-orange-600 hover:text-orange-700">
        ← Back to project
      </Link>
    </div>
  );
  if (!project) return <div className="p-10 text-center text-slate-400">Project not found</div>;

  const customerName = customers.find((c) => c.customer_id === project.customer_id)?.name || "—";
  const unit = units.find((u) => u.unit_id === project.unit_id) || null;
  const companyName = settings.company_name || "Anmol Metcomp Private Limited";
  // Round 21.2 — Use project's unit address if non-empty; else fall back to global company_address.
  const companyAddress = (unit && unit.address) ? unit.address : (settings.company_address || "");
  const children = project.children || [];
  const projectTotal = children.reduce((s, c) => s + (computeNpd(c).grand_total || 0), 0);

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden">
        <Link to={`/projects/${id}`} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
          <ArrowLeft size={16} /> Back to Project
        </Link>
        <div className="text-sm text-slate-500">Preview · {project.project_number}</div>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
          data-testid="print-btn"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto">
        <div className="bg-white shadow-md p-6 my-6 print:shadow-none print:my-0 print:p-0">
          <div className="quote-sheet text-slate-800 text-[12px]">
            <div className="grid grid-cols-[80px_1fr] border border-slate-700 mb-2">
              <div className="bg-white flex items-center justify-center p-1 row-span-2">
                <img src="/aampl-logo.png" alt="AAMPL" className="max-w-full max-h-[55px]" />
              </div>
              <div className="bg-orange-500 text-white font-bold text-center py-1.5 text-base">{companyName}</div>
              <div className="text-center italic text-slate-500 text-[11px] py-1 border-t border-slate-700">
                {companyAddress || "—"}
              </div>
            </div>

            <div className="bg-amber-200 text-slate-800 font-bold text-center py-1.5 border border-slate-700 text-sm mb-2">
              PROJECT QUOTATION — {project.project_number}
            </div>

            <table className="w-full border-collapse border border-slate-700 mb-2">
              <tbody>
                <tr>
                  <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Project Name</td>
                  <td className="border border-slate-700 p-1 px-2">{project.project_name || ""}</td>
                  <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">Customer</td>
                  <td className="border border-slate-700 p-1 px-2">{customerName}</td>
                </tr>
                <tr>
                  <td className="border border-slate-700 p-1 px-2 font-semibold">End Customer</td>
                  <td className="border border-slate-700 p-1 px-2">{project.end_customer || "—"}</td>
                  <td className="border border-slate-700 p-1 px-2 font-semibold">Due Date</td>
                  <td className="border border-slate-700 p-1 px-2">{project.customer_due_date || "—"}</td>
                </tr>
                <tr>
                  <td className="border border-slate-700 p-1 px-2 font-semibold">Validity</td>
                  <td className="border border-slate-700 p-1 px-2">{project.quote_validity_date || "—"}</td>
                  <td className="border border-slate-700 p-1 px-2 font-semibold">Date</td>
                  <td className="border border-slate-700 p-1 px-2">{new Date().toISOString().slice(0,10)}</td>
                </tr>
              </tbody>
            </table>

            <table className="w-full border-collapse border border-slate-700 mb-2">
              <thead className="bg-amber-100">
                <tr>
                  <th className="border border-slate-700 p-1 w-12">P#</th>
                  <th className="border border-slate-700 p-1 w-28">Part Number</th>
                  <th className="border border-slate-700 p-1 text-left">Part Name</th>
                  <th className="border border-slate-700 p-1 w-28 text-right">₹/pc</th>
                </tr>
              </thead>
              <tbody>
                {children.map((c) => {
                  const calc = computeNpd(c);
                  return (
                    <tr key={c.npd_id}>
                      <td className="border border-slate-700 p-1 text-center">P{c.child_index}</td>
                      <td className="border border-slate-700 p-1 px-2">{c.part_number || ""}</td>
                      <td className="border border-slate-700 p-1 px-2">{c.part_name || ""}</td>
                      <td className="border border-slate-700 p-1 px-2 text-right font-mono">{formatINR(calc.grand_total)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-orange-500 text-white font-bold">
                  <td colSpan={3} className="border border-slate-700 p-1.5">PROJECT TOTAL (sum of all parts ₹/pc)</td>
                  <td className="border border-slate-700 p-1.5 text-right font-mono">{formatINR(projectTotal)}</td>
                </tr>
              </tbody>
            </table>
            <p className="italic text-slate-500 text-[11px]">See following pages for per-part costing breakdown, terms, and signature.</p>

            <div className="mt-4">
              <ProjectSourcingRollup rollup={project.sourcing_rollup} />
            </div>
          </div>
        </div>

        {children.map((c) => (
          <div key={c.npd_id} className="bg-white shadow-md p-6 my-6 print:shadow-none print:my-0 print:p-0 print:break-before-page">
            <div className="font-bold text-slate-700 mb-2 text-base">
              Part P{c.child_index} — {c.part_name}
            </div>
            <NpdQuoteSheet npd={c} customerName={customerName} settings={settings} unit={unit} />
          </div>
        ))}
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .quote-sheet { font-size: 11px; }
          .print\\:break-before-page { break-before: page; page-break-before: always; }
        }
      `}</style>
    </div>
  );
}
